import { useMemo } from "react";
import { type CombinedError, useQuery } from "urql";
import {
  type ChatSearchConversation,
  SearchConversations,
} from "@/data-access/herald-chat/chat-queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

const CHAT_SEARCH_DEBOUNCE_MS = 300;
const CHAT_SEARCH_RESULT_LIMIT = 30;

export interface UseSearchChatConversationsParams {
  /** Raw search input; trimmed and debounced before querying. */
  query: string;
  /** When false, skips the request (e.g. while the search dialog is closed). */
  enabled?: boolean;
}

export interface UseSearchChatConversationsReturn {
  results: ChatSearchConversation[];
  totalCount: number;
  /** True when a debounced non-empty query is in flight. */
  isSearching: boolean;
  /** True after debounce when trimmed query is non-empty. */
  hasActiveQuery: boolean;
  error: CombinedError | undefined;
}

/**
 * Debounced title search for the chat command palette.
 * @example
 * ```tsx
 * const { results, isSearching, hasActiveQuery } = useSearchChatConversations({
 *   query,
 *   enabled: isOpen,
 * });
 * ```
 */
export const useSearchChatConversations = ({
  query,
  enabled = true,
}: UseSearchChatConversationsParams): UseSearchChatConversationsReturn => {
  const debouncedQuery = useDebouncedValue(
    query.trim(),
    CHAT_SEARCH_DEBOUNCE_MS,
  );
  const hasActiveQuery = debouncedQuery.length > 0;
  const shouldFetch = enabled && hasActiveQuery;

  const [{ data, fetching, error }] = useQuery({
    query: SearchConversations,
    variables: {
      filters: { title: debouncedQuery },
      pagination: { limit: CHAT_SEARCH_RESULT_LIMIT, offset: 0 },
    },
    pause: !shouldFetch,
  });

  const results = useMemo(
    () => data?.conversations.results ?? [],
    [data?.conversations.results],
  );

  return {
    results,
    totalCount: data?.conversations.totalCount ?? 0,
    isSearching: shouldFetch && fetching,
    hasActiveQuery,
    error,
  };
};
